import React, { useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import useProjectConfiguration from '../../hooks/useProjectConfiguration';
import ProductListSkeleton from '../Skeletons/ProductList';
import { useAuthGuard } from '../../hooks/useAuthGuard';
import { ensureProtocol } from '../../utils/netUtils';
import { apiActionError } from '../../utils/apiError';
import { buildProductCards } from '../../components/ProductCard';

import type { IProductData } from '../../types/affiliates.types';
import type { IProductCard } from '../../components/ProductCard';

import './style.scss';

function AccountSubscriptionsTab() {
  const [data, error, loading, redirectToProductRegister, reactivateProduct, getHint] =
    useProjectConfiguration();
  const { affServiceInstance, configState } = useAuthGuard();
  const cards = useMemo(
    () => buildProductCards(data?.client?.products || []),
    [data?.client?.products]
  );

  const handleActivateButtonClick = useCallback(async (card: IProductCard) => {
    await redirectToProductRegister(card.cps);
  }, [redirectToProductRegister]);

  const handleReactivateButtonClick = useCallback((card: IProductCard) => {
    reactivateProduct(card.cps, card.bindingId);
  }, [reactivateProduct]);

  const handleGetAccountRecovery = useCallback(async (item: IProductData) => {
    if (affServiceInstance && configState) {
      try {
        const jwtSession = localStorage.getItem('jwtSession');
        const result = await affServiceInstance.getRecoveryAccountUrl(
          item.cps,
          configState.deviceToken!,
          jwtSession!
        );

        if (result?.error) {
          throw apiActionError(result.error);
        }

        const urlWithProtocol = ensureProtocol(result!);
        window.open(urlWithProtocol, "_blank");
      } catch (e) {
        throw e;
      }
    }
  }, [affServiceInstance, configState]);

  function renderProductList() {
    return (
      <AnimatePresence>
        {(loading || !Boolean(data)) && (
          <ProductListSkeleton exit={{ opacity: 0 }} />
        )}
        {!loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {cards?.length > 0 ? (
              cards?.map((card) => (
                <card.Component
                  key={card.key}
                  data={card.primary}
                  logos={card.logos}
                  products={card.products}
                  onActivateButtonClick={() => handleActivateButtonClick(card)}
                  onReactivateButtonClick={() => handleReactivateButtonClick(card)}
                  onGetHint={() => getHint(card.primary.cps)}
                  onGetAccountRecovery={() => handleGetAccountRecovery(card.primary)}
                />
              ))
            ) : (
              <div className="AccountSubscriptionsTab__empty">
                <div className="AccountSubscriptionsTab__empty__container">
                  <h5>
                    Lo sentimos, tu plan actual no tiene acceso a estas plataformas de Streaming.
                  </h5>
                  <h6>¿Desea más entretención?</h6>
                  { !!data?.client && <a
                    href={data?.client.urls?.acquireServiceUrl}
                    target='_blank'
                    rel="noreferrer"
                  >
                    Contratar
                  </a>}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <div className="AccountSubscriptionsTab">
      <img src={require('./../../client/assets/claro.png')} alt="Claro_CL" />
      {!loading && cards && cards?.length > 0 && <h2>Accede ahora a tus aplicaciones disponibles</h2>}
      <article>
        {Boolean(error) ? (
          <div className="AccountSubscriptionsTab__empty">
            <h5>Ha ocurrido un error, intente nuevamente más tarde.</h5>
            <h5>Detalles:</h5>
            <code>
              {JSON.stringify(error, Object.getOwnPropertyNames(error))}
            </code>
          </div>
        ) : (
          renderProductList()
        )}
      </article>
    </div>
  );
}

export default AccountSubscriptionsTab;
